import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { initNetWorthSummary } from '@renenet/shared/src/actions/networth';
import { formatCurrencyByLocale } from '@renenet/shared/src/utils/currencyUtils';
import Tier from '@renenet/shared/src/constants/tiers';
import MILESTONES from '@renenet/shared/src/constants/milestones';

import NetWorthSummaryEmptyPlaceholder from './NetWorthSummaryEmptyPlaceholder.js'
import NetWorthTimeSeriesUploadContainer from './NetWorthTimeSeriesUploadContainer.js'
import NetWorthTimeframeSelector from './NetWorthTimeframeSelector.js'
import NetWorthTimeSeriesLine from './NetWorthTimeSeriesLine.js'
import NetWorthTimeSeriesTable from './NetWorthTimeSeriesTable.js'
import MilestoneCelebration from './MilestoneCelebration.js'

const LS_KEY = 'mp_celebrated_milestones';


function getCelebratedMilestones() {
  try { return new Set(JSON.parse(localStorage.getItem(LS_KEY) || '[]')); }
  catch { return new Set(); }
}

function saveCelebratedMilestones(set) {
  localStorage.setItem(LS_KEY, JSON.stringify([...set]));
}

function mapStateToProps (state) {
  return {
    currencyInfo: state.currencyInfo,
    netWorthInfo: state.netWorthInfo,
    userInfo: state.userInfo
  }
}

function mapDispatchToProps (dispatch) {
  return {
    initNetWorthSummary: (currencyInfo) => dispatch(initNetWorthSummary(currencyInfo))
  }
}

class NetWorth extends Component {
  constructor(props) {
    super(props);
    this.state = { celebrationMilestone: null };
  }

  async componentDidMount() {
    await this.props.initNetWorthSummary(this.props.currencyInfo);
    this.checkMilestones();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.netWorthInfo.currentNetWorth !== this.props.netWorthInfo.currentNetWorth ||
        prevProps.userInfo !== this.props.userInfo) {
      this.checkMilestones();
    }
  }

  checkMilestones() {
    const { netWorthInfo } = this.props;
    const netWorth = netWorthInfo.currentNetWorth;
    if (netWorth == null) return;

    // Compare directly in the user's aggregation currency so milestones
    // feel natural (EUR user celebrates at €100K, GBP user at £100K, etc.)
    const alreadyCelebrated = getCelebratedMilestones();

    const newMilestone = [...MILESTONES]
      .reverse()
      .find(m => netWorth >= m.amount && !alreadyCelebrated.has(String(m.amount)));

    if (newMilestone) {
      // Mark ALL crossed milestones as celebrated so lower ones never
      // surface on subsequent visits (only the highest new one is shown)
      const allCrossed = MILESTONES
        .filter(m => netWorth >= m.amount)
        .map(m => String(m.amount));
      saveCelebratedMilestones(new Set([...alreadyCelebrated, ...allCrossed]));
      this.setState({ celebrationMilestone: newMilestone });
    }
  }

  render() {
    const isPremium = this.props.userInfo && this.props.userInfo.tier === Tier.PREMIUM;

    return this.props.netWorthInfo.netWorthSummary === null
      ? (<Spinner animation="grow" variant="light" />)
      : (
        <Container>
          <MilestoneCelebration
            milestone={this.state.celebrationMilestone}
            currencyInfo={this.props.currencyInfo}
            onClose={() => this.setState({ celebrationMilestone: null })}
          />
          <Row>
            <Col><h2>Net Worth</h2></Col>
            <Col />
            {isPremium && <Col><NetWorthTimeSeriesUploadContainer /></Col>}
          </Row>
          {
            this.props.netWorthInfo.netWorthSummary.length > 0
            ? (
              <React.Fragment>
                <Row>
                  <Col><h4>Your current net worth is:</h4></Col>
                </Row>
                <Row>
                  <Col><h2>{formatCurrencyByLocale(this.props.currencyInfo.locale, this.props.currencyInfo.aggregationCurrency, this.props.netWorthInfo.currentNetWorth)}</h2></Col>
                  <Col />
                  <Col><NetWorthTimeframeSelector /></Col>
                </Row>
                <Row>
                  <NetWorthTimeSeriesLine />
                </Row>
                <Row>
                  <NetWorthTimeSeriesTable />
                </Row>
              </React.Fragment>
            ) : (
              <Row>
                <Col><NetWorthSummaryEmptyPlaceholder /></Col>
              </Row>
            )
          }
        </Container>
      )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(NetWorth);
